import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ImagePlus, RotateCcw } from 'lucide-react';
import { PREMIUM_PLAN } from '@anidex/shared/premium';
import { errorMessage } from '../services/api';
import { usePremiumImage, usePremiumImages } from '../services/queries/billing';
import { compressImage } from '../lib/compressImage';
import { useToasts } from './Toasts';
import { ErrorState, Spinner } from './States';


const PREMIUM_COMPRESSION = { quality: 0.85, maxSide: 2560, minBytes: 0 } as const;

export function PremiumImageUpload({
  kind, active, imageUrl, characterId, disabled = false,
}: {
  kind: 'avatar' | 'banner' | 'card';
  active: boolean;
  imageUrl?: string | null;
  characterId?: number;
  disabled?: boolean;
}) {
  const { t } = useTranslation('premium');
  const id = useId();
  const upload = usePremiumImage();
  const toasts = useToasts();
  const [compressing, setCompressing] = useState(false);
  const busy = upload.isPending || compressing;
  const run = async (file?: File) => {
    if (file && file.size > PREMIUM_PLAN.maxImageBytes) {
      toasts.push({ tone: 'error', title: t('images.failed'), description: t('images.tooLarge') });
      return;
    }

    let optimized = file;
    if (file) {
      setCompressing(true);
      try {
        optimized = await compressImage(file, PREMIUM_COMPRESSION);
      } catch {
        optimized = file;
      } finally {
        setCompressing(false);
      }
    }

    upload.mutate({ kind, characterId, file: optimized }, {
      onSuccess: () => toasts.push({ tone: 'success', title: file ? t('images.saved') : t('images.resetDone') }),
      onError: (error) => toasts.push({ tone: 'error', title: t('images.failed'), description: errorMessage(error) }),
    });
  };
  return (
    <div className="premium-image-upload">
      <h4><ImagePlus size={18} aria-hidden="true" />{t(`images.${kind}`)}</h4>
      <p className="hint">{t('images.hint', { max: PREMIUM_PLAN.maxCustomCardImages })}</p>
      {imageUrl && <img src={imageUrl} alt={t('images.preview')} className={`premium-image-upload__preview premium-image-upload__preview--${kind}`} />}
      {active ? (
        <label className="premium-image-upload__input" htmlFor={id}>
          <span>{busy ? t('images.saving') : t('images.choose')}</span>
          <input id={id} type="file" accept="image/png,image/jpeg,image/webp" disabled={disabled || busy} onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void run(file);
            event.target.value = '';
          }} />
        </label>
      ) : <Link to="/premium" className="text-link">{t('images.discover')}</Link>}
      {imageUrl && <button type="button" className="button button--small" disabled={disabled || busy} onClick={() => void run()}>
        <RotateCcw size={15} aria-hidden="true" />{t('images.reset')}
      </button>}
      <p className="hint">{t('images.rights')}</p>
    </div>
  );
}

export function PremiumImageGallery({ active }: { active: boolean }) {
  const { t } = useTranslation('premium');
  const images = usePremiumImages();
  const remove = usePremiumImage();
  const toasts = useToasts();
  if (images.isPending) return <Spinner />;
  if (images.isError) return <ErrorState error={images.error} onRetry={() => void images.refetch()} />;
  if (images.data.items.length === 0) return null;
  return (
    <section className="premium-gallery">
      <h3>{t('images.stored', { count: images.data.items.filter((image) => image.kind === 'card').length, max: PREMIUM_PLAN.maxCustomCardImages })}</h3>
      <p className="hint">{t('images.storedHint')}</p>
      <ul>{images.data.items.map((image) => <li key={image.url}>
        {active && <img src={image.url} alt="" width={40} height={40} />}
        <div><strong>{image.name ?? t(`images.${image.kind}`)}</strong>{!active && <small>{t('images.inactive')}</small>}
          {image.kind === 'card' && !image.owned && <small>{t('images.noLongerOwned')}</small>}</div>
        <button type="button" className="button button--small" disabled={image.locked || remove.isPending} onClick={() => remove.mutate({
          kind: image.kind, characterId: image.characterId ?? undefined,
        }, {
          onSuccess: () => toasts.push({ tone: 'success', title: t('images.resetDone') }),
          onError: (error) => toasts.push({ tone: 'error', title: t('images.failed'), description: errorMessage(error) }),
        })}>{t('images.remove')}</button>
      </li>)}</ul>
    </section>
  );
}
